import yasml from '@thirtytech/yasml';
import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useSeasonPricer } from '../../data/SeasonsPricer.state';
import { useMarketListings } from '../MarketListings/MarketListings.hooks';
import { GetTargetComparablePrice, GetTargetComparables } from '../MarketListings/marketListingsFilter';
import { applyFiltersAsync } from '../../workers/marketListingsFilterClient';
import { marketplaceListingsFilteredAtom } from '../../data/atoms';
import { calculateTargetPrice } from '../../utils/rule-utils';
import { BrokerNerdsBarkerCoreModelsInventoryListing, BrokerNerdsBarkerCoreModelsMarketplacesListing } from '../../api';
import { useGlobalState } from '../../data/Global.state';

function SeasonEventCardState({
  eventId,
  listing,
  setListing,
}: {
  eventId: string;
  listing: BrokerNerdsBarkerCoreModelsInventoryListing | null;
  setListing: Dispatch<SetStateAction<BrokerNerdsBarkerCoreModelsInventoryListing>>;
}) {
  const { rowData, unfilteredListings } = useMarketListings();
  const { rulesAtom, targetComparablePriceByEventIdAtom } = useSeasonPricer();
  // Share a single focused atom between read/write instead of building it twice with identical deps.
  const ruleAtom = useMemo(() => focusAtom(rulesAtom, (x) => x.prop(eventId)), [eventId, rulesAtom]);
  const rule = useAtomValue(ruleAtom);
  const updateRule = useSetAtom(ruleAtom);
  const filteredRowData = useAtomValue(marketplaceListingsFilteredAtom);
  const [innerFilteredRowData, setInnerFilteredRowData] = useState<BrokerNerdsBarkerCoreModelsMarketplacesListing[]>([]);
  useEffect(() => {
    let cancelled = false;
    applyFiltersAsync(filteredRowData, rule.filters, true, true, true).then((result) => {
      if (!cancelled) {
        setInnerFilteredRowData(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [filteredRowData, rule.filters]);
  const { tenants } = useGlobalState('tenants');
  const roundingSettings = tenants?.find((tenant) => tenant.tenantId === listing?.tenantId)?.settings?.pricerSettings?.roundPrices ?? 'None';

  const summaryTotals = useMemo(
    () => ({
      totalListings: unfilteredListings.length,
      totalFilteredListings: innerFilteredRowData.length,
      totalTickets: unfilteredListings.reduce((acc, _listing) => acc + (_listing.quantity || 0), 0),
      totalFilteredTickets: innerFilteredRowData.reduce((acc, _listing) => acc + (_listing.quantity || 0), 0),
    }),
    [innerFilteredRowData, unfilteredListings],
  );
  const targetComparables = useMemo(() => GetTargetComparables(innerFilteredRowData || [], rule.numComparables || 1), [innerFilteredRowData, rule.numComparables]);
  const targetPrice = GetTargetComparablePrice(targetComparables);

  const calculatedTargetPrice = useMemo(() => {
    if (listing) {
      return calculateTargetPrice({
        listing,
        offset: 0,
        rule,
        // Reuse the memoized targetComparables (same inputs) instead of recomputing GetTargetComparables.
        targetComparables,
        tenantRoundingSettings: roundingSettings,
      });
    }
    return 0;
  }, [targetComparables, listing, roundingSettings, rule]);

  const setTargetComparablePrice = useSetAtom(useMemo(() => focusAtom(targetComparablePriceByEventIdAtom, (x) => x.prop(eventId)), [eventId, targetComparablePriceByEventIdAtom]));

  useEffect(() => {
    if (listing) {
      const price = calculateTargetPrice({
        listing,
        offset: 0,
        rule,
        // Reuse the memoized targetComparables (same inputs) instead of recomputing GetTargetComparables.
        targetComparables,
        tenantRoundingSettings: roundingSettings,
      });
      setTargetComparablePrice(price);
    }
  }, [targetComparables, listing, roundingSettings, rule, setTargetComparablePrice]);

  return { targetPrice, calculatedTargetPrice, summaryTotals, rowData, updateRule, listing, setListing, targetComparables };
}

export const { Provider: SeasonEventCardProvider, useSelector: useSeasonEventCard } = yasml(SeasonEventCardState);
